import { useState, useCallback, createContext, useContext, useMemo } from 'react';
import { Icons } from '../utils/icons';
import { fontStack } from '../styles/shared';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'info', duration = 3000) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type, exiting: false }]);
        setTimeout(() => setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t)), duration - 300);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
        return id;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 300);
    }, []);

    const toast = useMemo(() => ({
        success: (msg, dur) => addToast(msg, 'success', dur),
        error: (msg, dur) => addToast(msg, 'error', dur),
        info: (msg, dur) => addToast(msg, 'info', dur),
        warning: (msg, dur) => addToast(msg, 'warning', dur),
    }), [addToast]);

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
}

const TYPE_STYLES = {
    success: { borderColor: 'rgba(16, 185, 129, 0.5)', iconBg: 'var(--success-bg)', iconColor: 'var(--success)' },
    error: { borderColor: 'rgba(239, 68, 68, 0.5)', iconBg: 'var(--error-bg)', iconColor: 'var(--error)' },
    warning: { borderColor: 'rgba(245, 158, 11, 0.5)', iconBg: 'var(--warning-bg)', iconColor: 'var(--warning)' },
    info: { borderColor: 'rgba(99, 102, 241, 0.5)', iconBg: 'var(--info-bg)', iconColor: 'var(--info)' },
};

function getIcon(type) {
    switch (type) {
        case 'success': return <Icons.Check />;
        case 'error': return <Icons.X />;
        case 'warning': return <span style={{ fontWeight: 700, fontSize: '14px' }}>!</span>;
        default: return <span style={{ fontWeight: 700, fontSize: '12px' }}>i</span>;
    }
}

function ToastContainer({ toasts, removeToast }) {
    if (toasts.length === 0) return null;

    return (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px', pointerEvents: 'none' }}>
            {toasts.map((t) => {
                const s = TYPE_STYLES[t.type] || TYPE_STYLES.info;
                return (
                    <div key={t.id} onClick={() => removeToast(t.id)} style={{
                        display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px',
                        background: 'var(--toast-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                        border: `1px solid ${s.borderColor}`, borderRadius: '12px', boxShadow: 'var(--card-shadow)',
                        color: 'var(--text)', fontSize: '13px', fontFamily: fontStack, pointerEvents: 'auto', cursor: 'pointer',
                        animation: t.exiting ? 'toastSlideOut 0.3s ease-in forwards' : 'toastSlideIn 0.3s ease-out forwards',
                        maxWidth: '400px',
                    }}>
                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: s.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.iconColor, flexShrink: 0 }}>
                            {getIcon(t.type)}
                        </div>
                        <span style={{ flex: 1 }}>{t.message}</span>
                    </div>
                );
            })}
            <style>{`
                @keyframes toastSlideIn { from { opacity:0; transform:translateY(20px) scale(.95); } to { opacity:1; transform:translateY(0) scale(1); } }
                @keyframes toastSlideOut { from { opacity:1; transform:translateY(0) scale(1); } to { opacity:0; transform:translateY(10px) scale(.95); } }
            `}</style>
        </div>
    );
}

export function useToast() { return useContext(ToastContext); }
